/**
 * ------------------------------------------------------------------------------------------------
 * CART DISABLED
 * ------------------------------------------------------------------------------------------------
 * This store sells through Amazon. Native Shopify checkout is turned off, so this script makes the
 * cart unusable at runtime, on top of the markup that "cart-disabled.liquid" already removes.
 *
 * It must run BEFORE vendor.js / theme.js / custom.js (they are deferred, this one is not) so that
 * the patched `fetch` and `XMLHttpRequest` are in place before any cart code can call them.
 *
 * Configuration comes from `window.themeCartDisabled`, rendered by snippets/cart-disabled.liquid.
 * Toggle the whole thing off with the "Disable cart and checkout" theme setting.
 * ------------------------------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var config = window.themeCartDisabled || {};

  if (!config.enabled) {
    return;
  }

  var root = (config.rootUrl || '/').replace(/\/$/, '');
  var redirectUrl = config.redirectUrl || root + '/';

  // Blocked add to cart: `false` (the default) drops the request and empties the cart with a
  // background call, which never leaves the page. `true` sends the browser to /cart/clear instead.
  //
  // WARNING: only turn the redirect on if you are sure nothing calls the cart API on page load.
  // An app that writes to the cart on every page would otherwise bounce the customer between
  // /cart/clear and the page they came from. The loop guard below caps the damage either way.
  var REDIRECT_ON_BLOCKED_ADD = config.redirectOnBlockedAdd === true;

  var nativeFetch = window.fetch;
  var isNavigating = false;

  /**
   * ---------------------------------------------------------------------------------------------
   * URL HELPERS
   * ---------------------------------------------------------------------------------------------
   */

  function toPath(url) {
    try {
      return new URL(String(url), window.location.origin).pathname;
    } catch (e) {
      return String(url || '');
    }
  }

  /** Drops the locale prefix so `/en-gb/cart` and `/cart` are compared the same way. */
  function toRelativePath(url) {
    var path = toPath(url);

    if (root && path.toLowerCase().indexOf(root.toLowerCase() + '/') === 0) {
      path = path.slice(root.length);
    }

    return path;
  }

  // /cart/add, /cart/change, /cart/update (+ .js/.json), in any locale sub-path. /cart/clear is
  // deliberately absent: it is the escape hatch we rely on.
  var MUTATING_CART_PATH = /\/cart\/(add|change|update)(\.js|\.json)?$/i;

  // Anchored, so a content page such as /pages/cart or /blogs/news/checkout is left alone
  var CART_PAGE_PATH = /^\/cart\/?$/i;
  var CHECKOUT_PATH = /^\/checkouts?(\/|$)/i;

  function isMutatingCartUrl(url) {
    return MUTATING_CART_PATH.test(toPath(url));
  }

  function isCartPageOrCheckoutUrl(url) {
    var path = toRelativePath(url);
    return CART_PAGE_PATH.test(path) || CHECKOUT_PATH.test(path);
  }

  function isCartOrCheckoutUrl(url) {
    return isCartPageOrCheckoutUrl(url) || isMutatingCartUrl(url);
  }

  /**
   * Builds the /cart/clear URL. `return_to` keeps the customer where they were instead of dropping
   * them back on the (now unreachable) cart page.
   */
  function clearUrl(returnTo) {
    var destination = returnTo || window.location.pathname + window.location.search;
    return root + '/cart/clear?return_to=' + encodeURIComponent(destination);
  }

  // A misconfigured redirect target that points back at the cart would bounce forever
  if (isCartPageOrCheckoutUrl(redirectUrl)) {
    redirectUrl = root + '/';
  }

  /**
   * ---------------------------------------------------------------------------------------------
   * ACTIONS
   * ---------------------------------------------------------------------------------------------
   */

  /**
   * Circuit breaker. Every redirect this script performs is stamped in sessionStorage; once a few
   * pile up in a short window we stop redirecting altogether. Whatever is triggering them (an app
   * writing to the cart on page load, a `return_to` the platform ignores) then degrades to a
   * silently blocked request instead of trapping the customer in a redirect loop.
   */
  var LOOP_STORAGE_KEY = 'theme:cart-disabled:redirects';
  var LOOP_MAX_REDIRECTS = 3;
  var LOOP_WINDOW_MS = 15000;

  function loopGuardAllowsRedirect() {
    try {
      var now = Date.now();
      var stamps = JSON.parse(window.sessionStorage.getItem(LOOP_STORAGE_KEY) || '[]').filter(function (stamp) {
        return now - stamp < LOOP_WINDOW_MS;
      });

      if (stamps.length >= LOOP_MAX_REDIRECTS) {
        window.sessionStorage.setItem(LOOP_STORAGE_KEY, JSON.stringify(stamps));
        return false;
      }

      stamps.push(now);
      window.sessionStorage.setItem(LOOP_STORAGE_KEY, JSON.stringify(stamps));
      return true;
    } catch (e) {
      // Private browsing can refuse storage. Fall back to the per-page-load guard only.
      return true;
    }
  }

  function navigateTo(url) {
    if (isNavigating || !loopGuardAllowsRedirect()) {
      return false;
    }

    isNavigating = true;
    window.location.replace(url);
    return true;
  }

  /** Empties the cart without leaving the page. Used when we do not want a hard redirect. */
  function clearCartSilently() {
    if (typeof nativeFetch !== 'function') {
      return;
    }

    nativeFetch
      .call(window, root + '/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: '{}'
      })
      .catch(function () {
        // The cart page is unreachable anyway, nothing useful to do here
      });
  }

  /**
   * Called every time something tries to put a product in the cart. Returns true when it redirected,
   * false when the request was simply dropped: `navigateTo` refuses once the loop guard trips, and
   * the request still has to be dealt with rather than left half applied.
   */
  function onBlockedAddToCart() {
    if (REDIRECT_ON_BLOCKED_ADD && navigateTo(clearUrl())) {
      return true;
    }

    clearCartSilently();
    return false;
  }

  /** Sends someone who reached the cart or the checkout somewhere they can actually buy. */
  function leaveCart() {
    clearCartSilently();

    // Straight to the destination, never through /cart/clear: if the platform ignored `return_to`
    // we would land back on /cart and start over.
    navigateTo(redirectUrl);
  }

  /**
   * ---------------------------------------------------------------------------------------------
   * NETWORK INTERCEPTION (Ajax cart API)
   * ---------------------------------------------------------------------------------------------
   */

  if (typeof nativeFetch === 'function') {
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';

      if (isMutatingCartUrl(url)) {
        // When a redirect is under way, a promise that never settles keeps the caller in its
        // loading state instead of flashing an error on a page that is about to be replaced.
        // Without one, the caller has to be told the request failed, or it waits forever.
        return onBlockedAddToCart()
          ? new Promise(function () {})
          : Promise.reject(new Error('Cart is disabled on this store.'));
      }

      return nativeFetch.apply(this, arguments);
    };
  }

  var nativeOpen = window.XMLHttpRequest && window.XMLHttpRequest.prototype.open;
  var nativeSend = window.XMLHttpRequest && window.XMLHttpRequest.prototype.send;

  if (nativeOpen && nativeSend) {
    window.XMLHttpRequest.prototype.open = function (method, url) {
      this.cartDisabledBlocked = isMutatingCartUrl(url);
      return nativeOpen.apply(this, arguments);
    };

    window.XMLHttpRequest.prototype.send = function () {
      if (this.cartDisabledBlocked) {
        onBlockedAddToCart();
        return;
      }

      return nativeSend.apply(this, arguments);
    };
  }

  /**
   * ---------------------------------------------------------------------------------------------
   * DOM INTERCEPTION (native form posts, links, buttons)
   * ---------------------------------------------------------------------------------------------
   */

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;

      if (!form || form.tagName !== 'FORM') {
        return;
      }

      var action = form.getAttribute('action') || '';

      if (isMutatingCartUrl(action)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        onBlockedAddToCart();
        return;
      }

      // The cart page posts to /cart with a `checkout` input to reach the checkout
      if (isCartPageOrCheckoutUrl(action)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        leaveCart();
      }
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;

      if (!target || typeof target.closest !== 'function') {
        return;
      }

      if (target.closest('[name="checkout"], .checkout-button, .shopify-payment-button, .additional-checkout-buttons')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        leaveCart();
        return;
      }

      if (target.closest('[data-product-add-to-cart-button], .product-form__add-button')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        onBlockedAddToCart();
        return;
      }

      var link = target.closest('a[href]');

      if (link && isCartOrCheckoutUrl(link.getAttribute('href'))) {
        event.preventDefault();
        event.stopImmediatePropagation();
        leaveCart();
      }
    },
    true
  );

  /**
   * ---------------------------------------------------------------------------------------------
   * APP-INJECTED MARKUP
   * ---------------------------------------------------------------------------------------------
   * Apps (subscriptions, upsells, reviews...) add their own add to cart forms after page load. The
   * stylesheet hides `[type="submit"]` inside them; a plain `<button>` with no type submits too, so
   * it is tagged here with `data-cart-disabled-button` for the same rule to pick up.
   */

  function neutralize() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]:not([data-cart-disabled])');

    for (var i = 0; i < forms.length; i++) {
      forms[i].setAttribute('data-cart-disabled', 'true');

      var buttons = forms[i].querySelectorAll('button');

      for (var j = 0; j < buttons.length; j++) {
        if (buttons[j].type === 'submit') {
          buttons[j].setAttribute('data-cart-disabled-button', 'true');
        }
      }
    }
  }

  function observe() {
    neutralize();

    if (typeof MutationObserver !== 'function') {
      return;
    }

    new MutationObserver(neutralize).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }

  /**
   * ---------------------------------------------------------------------------------------------
   * PAGE GUARD
   * ---------------------------------------------------------------------------------------------
   * Anyone landing on /cart or /checkout is sent to the destination page and their cart is emptied
   * behind them. Because the cart is kept empty, Shopify's own checkout is unreachable even from a
   * bookmarked link.
   */

  if (isCartPageOrCheckoutUrl(window.location.pathname)) {
    leaveCart();
  } else if (config.cartItemCount > 0) {
    // Leftover items from before the cart was disabled, or added server side by an app
    clearCartSilently();
  }
})();
